// Micro event dictionary: tiny animations for every gameplay action.
// Maps event types to particle/animation spawns so births, deaths, pickups,
// hits, movement, and level-ups all read instantly.

import * as THREE from 'three';
import type { Vec2 } from '../types.js';
import { ParticleSystem } from './particles.js';
import { TrailSystem } from './trails.js';
import { DamageNumbers } from './damage-numbers.js';
import { FloatingText } from './floating-text.js';
import { roleHueFor } from './instances.js';
import { SKILL_META, skillEffectSummary, type SkillId } from '../captain/skills.js';
import type { GameEvent } from '../sim/events.js';

export interface MicroEventContext {
  playerPos: Vec2;
  playerVel: Vec2;
}

/** Mote collects inside one 200 ms window are summed into a single floating
 * "+N" and one burst, so vacuum chains don't spam a dozen stamped popups. */
const PICKUP_COALESCE_S = 0.2;
/** The salvage "+N" chip re-arms no faster than this, so a vacuum chain
 * updates one docked readout instead of stamping a stream of popups. */
const MOTE_CHIP_COOLDOWN_S = 0.45;
/** Center-plate declutter: floating chips spawn ABOVE the ship, clear of the
 * hull — the player must stay findable mid-combat. Slots are staggered by
 * more than a chip height (the big chips are ~1.6 u tall) so a level-up's
 * LUMENSURGE and a skill pip never overlap. (The salvage "+N" read left the
 * world space entirely — it's the DOM chip docked at the top edge.) */
const PLATE_LIFT_TOAST = 4.0;
const PLATE_LIFT_BIG = 5.6;

/** Kill reward gold: the shard fade default and the hue every red-family
 * victim collapses to (red/pink stay reserved for live threats). */
const KILL_TINT_GOLD = 0xffd36b;

/** Shard fade hue for a kill: the victim's role hue (instances.ts), so a kill
 * cone cools white-hot → ONE family hue. The red/pink threat sector
 * (≥325° or <25°) collapses to gold — red never appears in kill FX, so the
 * only red objects inside a kill region stay the live enemies. */
function killTintFor(defId: unknown): number {
  const hue = typeof defId === 'string' ? roleHueFor(defId) : undefined;
  if (hue === undefined) return KILL_TINT_GOLD;
  const deg = new THREE.Color(hue).getHSL({ h: 0, s: 0, l: 0 }).h * 360;
  return deg >= 325 || deg < 25 ? KILL_TINT_GOLD : hue;
}

export class MicroEvents {
  private readonly vignette: HTMLDivElement;
  /** Salvage "+N" readout: a DOM chip docked at the top edge under the XP
   * strip (green = XP family), fading in ≤600 ms — the world-space plate it
   * replaced lingered ~1.5 s over the ship, the longest-lived world element. */
  private readonly moteChipEl: HTMLDivElement;
  private prevSpeed = 0;
  /** Coalesce buffer for xp-mote pickups (see PICKUP_COALESCE_S). */
  private pendingMoteValue = 0;
  private pendingMotePos: Vec2 | null = null;
  private moteFlushIn = 0;
  private moteChipCooldown = 0;
  /** Last event-context player position: the mote chip clamps to it. */
  private lastPlayerPos: Vec2 = { x: 0, y: 0 };

  constructor(
    private readonly particles: ParticleSystem,
    private readonly trails: TrailSystem,
    private readonly damageNumbers: DamageNumbers,
    private readonly floatingText: FloatingText,
    private readonly scene: THREE.Scene
  ) {
    // Red damage vignette flash on player hit.
    this.vignette = document.createElement('div');
    this.vignette.style.cssText = `
      position: absolute; inset: 0; pointer-events: none;
      background: radial-gradient(circle, transparent 30%, rgba(255, 40, 60, 0.35) 100%);
      opacity: 0; transition: opacity 0.25s ease-out; z-index: 30;
    `;
    document.getElementById('ui-layer')?.appendChild(this.vignette);
    // Docked salvage chip (see the field note). Inline styles like the
    // vignette; the XP green ties it to the top-edge XP strip it sits under.
    this.moteChipEl = document.createElement('div');
    this.moteChipEl.style.cssText = `
      position: absolute; top: 14px; top: calc(14px + env(safe-area-inset-top, 0px)); left: 50%;
      transform: translateX(-50%); pointer-events: none; z-index: 20;
      font-family: 'Silkscreen', monospace; font-size: 11px; line-height: 1.3;
      color: #7dff4f; text-shadow: 0 2px 0 rgba(0, 0, 0, 0.7); opacity: 0;
    `;
    document.getElementById('ui-layer')?.appendChild(this.moteChipEl);
  }

  onEvent(e: GameEvent, ctx: MicroEventContext): void {
    this.lastPlayerPos = ctx.playerPos;
    switch (e.type) {
      case 'spawn': {
        if (e.payload.kind === 'enemy') {
          const pos = this.posFromPayload(e.payload);
          if (pos) this.particles.spawnFlash(pos, 0xb45cff, 0.8, 0.2, 1.0);
        } else if (e.payload.kind === 'projectile') {
          // Muzzle flash blooms on the ship, not on the projectile's static
          // birth point — otherwise moving shots read as detached origins.
          this.particles.spawnMuzzle(ctx.playerPos);
        }
        break;
      }
      case 'death': {
        const pos = this.posFromPayload(e.payload);
        if (!pos) break;
        // Hue discipline: destruction reads WHITE → the victim's FAMILY hue
        // (red/pink collapse to gold — killTintFor), so each kill cone is
        // hue-coherent and a dart inside a kill region is still the only red
        // object there. Elites and bosses get the scaled-up staging (bigger
        // pop, more shards, the thin gold ring — the ring stays gold on every
        // kill, the heavy-kill read).
        const tint = killTintFor(e.payload.def_id);
        if (e.payload.boss === true || e.payload.elite === true) {
          this.particles.spawnDeathElite(pos, tint);
        } else {
          this.particles.spawnDeath(pos, tint);
        }
        // Soul mote flies to the player for point-gathering feedback — gold,
        // the reserved reward hue: loot flying home, not another red spark.
        this.particles.spawnSoul(pos, 0xffd36b, ctx.playerPos);
        break;
      }
      case 'hit': {
        const pos = this.posFromPayload(e.payload);
        if (!pos) break;
        this.particles.spawnHit(pos);
        const damage = typeof e.payload.damage === 'number' ? e.payload.damage : 0;
        const crit = !!e.payload.crit;
        if (damage > 0) this.damageNumbers.spawn(pos, damage, crit);
        break;
      }
      case 'pickup': {
        const pos = this.posFromPayload(e.payload);
        if (!pos) break;
        const kind = e.payload.kind;
        if (kind === 'xp_mote') {
          // Per-pickup spark (the chain sparkles per mote; it just doesn't
          // COUNT per mote) — then bank the value into the OPEN 200 ms
          // window. The window must NOT be re-armed per mote — at high
          // pickup rates it would never close and the "+N" read would never
          // fire again. (Window semantics borrowed from the claude build's
          // renderer mote coalescing.)
          this.particles.spawnPickup(pos);
          const value = typeof e.payload.value === 'number' ? e.payload.value : 1;
          this.pendingMoteValue += value;
          this.pendingMotePos = pos;
          if (this.moteFlushIn <= 0) this.moteFlushIn = PICKUP_COALESCE_S;
        } else {
          // Rare pickups (health) keep their immediate per-event burst.
          this.particles.spawnPickup(pos);
        }
        break;
      }
      case 'player_hit': {
        this.flashVignette();
        break;
      }
      case 'levelup_offer': {
        const tier = typeof e.payload.tier === 'number' ? e.payload.tier : 0;
        if (tier > 0) {
          // Gold like every reward read (kill FX, banners, the level chip) —
          // green stays reserved for the XP meter and pickup elements.
          // worldScale 0.62: the plate re-spawns on every wave-clear vac
          // train — at full size it parked over the ship as the loudest
          // persistent object on screen (art-review minor).
          this.floatingText.spawn(this.abovePlayer(PLATE_LIFT_BIG), `LUMENSURGE ×${tier}`, '#ffd36b', 28, false, 0.62);
          // Confetti burst.
          for (let i = 0; i < 12; i++) {
            const angle = (i / 12) * Math.PI * 2;
            const dir = { x: Math.cos(angle), y: Math.sin(angle) };
            this.particles.spawnDust(ctx.playerPos, dir);
          }
        }
        break;
      }
      case 'boss_spawn': {
        // Boss entrance: a hot pink flare + shard burst at the spawn point,
        // readable even off-view (the radar arrow points the way).
        const pos = this.posFromPayload(e.payload);
        if (!pos) break;
        this.particles.spawnFlash(pos, 0xff3d81, 3.2, 0.45, 1.8);
        this.particles.spawnDeathShards(pos, 0xff3d81);
        this.floatingText.spawn(pos, '⚠', '#ff3d81', 30);
        break;
      }
      case 'captain_skill': {
        // Captain skill pip: a small toast over the ship announcing WHAT
        // improved ("TAC ★2 · +4% DMG") — ability changes never land silently.
        const skill = typeof e.payload.skill === 'string' ? e.payload.skill : '';
        const pips = typeof e.payload.pips === 'number' ? e.payload.pips : 0;
        const meta = SKILL_META[skill as SkillId];
        if (meta && pips > 0) {
          const fx = skillEffectSummary(skill as SkillId, pips);
          this.floatingText.spawn(this.abovePlayer(PLATE_LIFT_TOAST), `${meta.label} ★${pips} · ${fx}`, '#ffd36b', 14, false, 0.75);
        }
        break;
      }
      case 'dash': {
        const phase = e.payload.phase;
        const pos = this.posFromPayload(e.payload);
        const dirRaw = e.payload.dir as { x?: unknown; y?: unknown } | undefined;
        const dir: Vec2 | undefined =
          dirRaw && typeof dirRaw.x === 'number' && typeof dirRaw.y === 'number'
            ? { x: dirRaw.x, y: dirRaw.y }
            : undefined;
        if (phase === 'start' && pos && dir) {
          this.trails.startDash(pos, dir, 0.85, 0xffe9a8);
          for (let i = 0; i < 6; i++) {
            this.particles.spawnDust(pos, dir);
          }
        } else if (phase === 'end') {
          this.trails.endDash();
        }
        break;
      }
    }
  }

  /** Track movement start/stop for dust puffs. */
  onMove(ctx: MicroEventContext): void {
    const speed = Math.hypot(ctx.playerVel.x, ctx.playerVel.y);
    if (this.prevSpeed < 0.5 && speed >= 0.5) {
      this.particles.spawnDust(ctx.playerPos, ctx.playerVel);
    } else if (this.prevSpeed >= 0.5 && speed < 0.5) {
      const back = { x: -ctx.playerVel.x, y: -ctx.playerVel.y };
      this.particles.spawnDust(ctx.playerPos, back);
    }
    this.prevSpeed = speed;
  }

  update(dt: number): void {
    this.floatingText.update(dt);
    if (this.moteChipCooldown > 0) this.moteChipCooldown -= dt;
    // Fire the coalesced mote read when the 200 ms window closes: one small
    // "+total" docked at the top edge under the XP strip (green = XP family).
    // The burst itself already fired per pickup; the window just sums the
    // COUNT. The chip re-arms on a cooldown, so vacuum chains can't paper a
    // plate over the player — and its whole hold+fade is ≤600 ms, never the
    // longest-lived element on screen.
    if (this.moteFlushIn > 0) {
      this.moteFlushIn -= dt;
      if (this.moteFlushIn <= 0 && this.pendingMotePos) {
        // 0-value motes exist in the sim (e.g. surge trickles): a "+0" read
        // carries no information — suppress it, and round fractional sums.
        const total = Math.round(this.pendingMoteValue);
        if (total >= 1 && this.moteChipCooldown <= 0) {
          this.flashMoteChip(total);
          this.moteChipCooldown = MOTE_CHIP_COOLDOWN_S;
        }
        this.pendingMoteValue = 0;
        this.pendingMotePos = null;
      }
    }
  }

  dispose(): void {
    this.floatingText.dispose();
    this.vignette.remove();
    this.moteChipEl.remove();
  }

  private flashVignette(): void {
    this.vignette.style.opacity = '1';
    setTimeout(() => {
      this.vignette.style.opacity = '0';
    }, 60);
  }

  /** Docked salvage read: snap the "+N" chip visible, hold 300 ms, fade
   * 250 ms — gone in ≤600 ms. The reflow restarts the cycle on a re-arm. */
  private flashMoteChip(total: number): void {
    const el = this.moteChipEl;
    el.textContent = `+${total}`;
    const s = el.style;
    s.transition = 'none';
    s.opacity = '1';
    void el.offsetWidth;
    s.transition = 'opacity 0.25s ease-in 0.3s';
    s.opacity = '0';
  }

  /** Floating-text slot above the ship: center chips clear the hull so the
   * player silhouette stays readable under them. */
  private abovePlayer(lift: number): Vec2 {
    return { x: this.lastPlayerPos.x, y: this.lastPlayerPos.y + lift };
  }

  private posFromPayload(p: Record<string, unknown>): Vec2 | undefined {
    if (typeof p.x === 'number' && typeof p.y === 'number') {
      return { x: p.x, y: p.y };
    }
    const pos = p.pos as Record<string, unknown> | undefined;
    if (pos && typeof pos.x === 'number' && typeof pos.y === 'number') {
      return { x: pos.x, y: pos.y };
    }
    return undefined;
  }
}
